import Link from 'next/link'
import { useEffect } from 'react'
import Stars from 'components/Stars'
import { useMe } from 'lib/useMe'
import { Button } from 'components/Button'
import { Card } from 'components/ui/Card'

/**
 * Modern minimalist landing page for CheapVeep VPN service
 * @description Clean, professional landing page with hero section, server locations, and pricing
 * @returns {JSX.Element} The modernized home page with cyberpunk aesthetics
 */
export default function Home() {
  let me = useMe()
  useEffect(() => {
    if (me) {
      window.document.location = `/account`
    }
  }, [me])

  const serverLocations = [
    { city: 'Los Angeles', region: 'West Coast', latency: '< 20ms' },
    { city: 'Chicago', region: 'Central', latency: '< 15ms' },
    { city: 'New York', region: 'East Coast', latency: '< 18ms' }
  ]

  return (
    <div id="index" className="h-screen relative">
      <Stars>
        {/* Navigation */}
        <div className='absolute top-0 right-0 p-6 z-10'>
          <Link href='/login' className='text-lg font-medium hover:text-cyan-300 transition-colors'>
            Sign In
          </Link>
        </div>

        {/* Main Content */}
        <div className='flex flex-col items-center min-h-screen px-4 md:px-6 space-y-12 md:space-y-16 z-10 h-screen overflow-auto pb-16'>

          {/* Hero Section */}
          <div className='text-center space-y-6 md:space-y-8 max-w-4xl'>
            <h1 className='text-4xl sm:text-6xl md:text-8xl font-bold text-white mb-4 md:mb-6'>
              Cheap<span className='text-cyan-400'>Veep</span>
            </h1>

            <p className='text-lg sm:text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto leading-relaxed px-4'>
              Modern WireGuard VPN with lightning-fast speeds and simple pricing
            </p>

            <div className='flex flex-col sm:flex-row gap-4 justify-center items-center mt-6 md:mt-8'>
              <Link href='/signup'>
                <Button mode='primary' size='lg' className='px-6 md:px-8 py-3 md:py-4 text-base md:text-lg'>
                  Get Started Now
                </Button>
              </Link>
              <div className='text-cyan-400 text-base md:text-lg font-medium'>
                Only $5/month
              </div>
            </div>
          </div>

          {/* Server Locations */}
          <div className='w-full max-w-5xl'>
            <h2 className='text-2xl md:text-3xl font-bold text-center text-white mb-6 md:mb-8'>
              Premium Server Locations
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6'>
              {serverLocations.map((location, index) => (
                <Card key={index} mode='secondary' className='text-center p-4 md:p-6 hover:scale-105 transition-transform'>
                  <div className='space-y-2 md:space-y-3'>
                    <h3 className='text-lg md:text-xl font-bold text-cyan-400'>{location.city}</h3>
                    <p className='text-sm md:text-base text-gray-300'>{location.region}</p>
                    <div className='text-xs md:text-sm text-fuchsia-400 font-medium'>
                      {location.latency} latency
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* WireGuard Protocol Advantage */}
          <div className='w-full max-w-4xl text-center'>
            <h2 className='text-2xl md:text-3xl font-bold text-white mb-6'>Next-Gen WireGuard Protocol</h2>
            <p className='text-gray-300 mb-8 text-lg'>Up to 15x faster than traditional VPN protocols with better battery life and modern cryptography</p>

            <div className='flex flex-col md:flex-row gap-8 mb-8 justify-center'>
              <div className='text-left space-y-3'>
                <h4 className='text-cyan-400 font-bold text-lg'>⚡ Performance Benefits</h4>
                <ul className='text-gray-300 space-y-2 text-sm'>
                  <li>• 4-8% bandwidth overhead vs 20-25% for legacy protocols</li>
                  <li>• Multi-threaded architecture for maximum speed</li>
                  <li>• Seamless network switching and roaming</li>
                  <li>• 50% less battery drain on mobile devices</li>
                </ul>
              </div>
              <div className='text-left space-y-3'>
                <h4 className='text-fuchsia-400 font-bold text-lg'>🔒 Security Advantages</h4>
                <ul className='text-gray-300 space-y-2 text-sm'>
                  <li>• State-of-the-art ChaCha20 encryption</li>
                  <li>• Minimal attack surface with 4,000 lines of code</li>
                  <li>• Perfect forward secrecy for all connections</li>
                  <li>• Formal verification of cryptographic protocols</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className='w-full max-w-4xl'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 text-center'>
              <div className='space-y-2 md:space-y-3'>
                <div className='text-3xl md:text-4xl text-cyan-400 font-bold'>1TB</div>
                <h3 className='text-base md:text-lg font-medium text-white'>Monthly Data</h3>
                <p className='text-gray-400 text-sm'>Generous quota with affordable overages</p>
              </div>

              <div className='space-y-2 md:space-y-3'>
                <div className='text-3xl md:text-4xl text-fuchsia-400 font-bold'>🛡️</div>
                <h3 className='text-base md:text-lg font-medium text-white'>Military Encryption</h3>
                <p className='text-gray-400 text-sm'>ChaCha20 cryptographic security</p>
              </div>

              <div className='space-y-2 md:space-y-3'>
                <div className='text-3xl md:text-4xl text-cyan-400 font-bold'>📱</div>
                <h3 className='text-base md:text-lg font-medium text-white'>All Devices</h3>
                <p className='text-gray-400 text-sm'>Any device, any OS, any platform</p>
              </div>
            </div>
          </div>

          {/* Pricing CTA */}
          <div className='text-center space-y-6 max-w-2xl w-full'>
            <Card mode='secondary' className='p-6 md:p-8'>
              <div className='space-y-4 md:space-y-6'>
                <h3 className='text-xl md:text-2xl font-bold text-white'>Simple Pricing</h3>
                <div className='text-3xl md:text-4xl font-bold text-fuchsia-400'>$5<span className='text-base md:text-lg text-gray-400'>/month</span></div>
                <p className='text-sm md:text-base text-gray-300'>Monthly billing • Cancel anytime • No contracts</p>
                <Link href='/signup' className='block'>
                  <Button mode='secondary' size='lg' className='w-full'>
                    Start Your VPN Journey
                  </Button>
                </Link>
              </div>
            </Card>
          </div>

        </div>
      </Stars>
    </div>
  )
}

export async function getServerSideProps({ res }) {
  // Set no-cache headers for the index page
  res.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate')
  res.setHeader('Pragma', 'no-cache')
  res.setHeader('Expires', '0')

  return {
    props: {}
  }
}
